import React from "react";
import { Helmet } from "react-helmet-async";

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonical,
  ogType = "website",
  ogImage = "https://farnhamanddistrictwindowcleaning.uk/largeLogo512.jpg",
}) => {
  const siteName = "Farnham and District Window Cleaning";
  const fullTitle = title ? `${title} | ${siteName}` : siteName;
  const defaultDescription =
    "Professional window and exterior cleaning services in Farnham, Aldershot, Alton and surrounding areas. Trusted since 1972.";
  const metaDescription = description || defaultDescription;
  const url = canonical || "https://farnhamanddistrictwindowcleaning.uk";

  // Local Business Schema
  const schemaOrgJSONLD = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: siteName,
    image: ogImage,
    "@id": "https://farnhamanddistrictwindowcleaning.uk",
    url: "https://farnhamanddistrictwindowcleaning.uk",
    telephone: "01252 726833",
    priceRange: "£",
    address: {
      "@type": "PostalAddress",
      streetAddress: "",
      addressLocality: "Farnham",
      addressRegion: "Surrey",
      postalCode: "",
      addressCountry: "GB",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 51.2138,
      longitude: -0.7994,
    },
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ],
      opens: "08:00",
      closes: "18:00",
    },
    sameAs: ["https://www.facebook.com/profile.php?id=100054523986920"],
  };

  return (
    <Helmet>
      {/* Standard Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={siteName} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(schemaOrgJSONLD)}
      </script>
    </Helmet>
  );
};

export default SEO;
